/**
 * Industry Template Service
 * Manages industry-specific campaign templates for AI-driven automation
 * Google Ads API v22 Compatible
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  increment,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { IndustryTemplate, IndustryType, TemplateValidationResult, TemplateOptimizationSuggestion } from '../types/industryTemplate';
import { getClient } from './geminiService';

export const industryTemplateService = {
  /**
   * Get all templates
   */
  async getAllTemplates(): Promise<IndustryTemplate[]> {
    try {
      const templatesRef = collection(db, 'industryTemplates');
      const q = query(templatesRef, where('isActive', '==', true), orderBy('industry'));
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        lastOptimizationCheck: doc.data().lastOptimizationCheck?.toDate(),
        lastUsedAt: doc.data().lastUsedAt?.toDate(),
        versionHistory: (doc.data().versionHistory || []).map((v: any) => ({
          ...v,
          createdAt: v.createdAt?.toDate()
        })),
        pendingSuggestions: (doc.data().pendingSuggestions || []).map((s: any) => ({
          ...s,
          createdAt: s.createdAt?.toDate(),
          reviewedAt: s.reviewedAt?.toDate()
        }))
      } as IndustryTemplate));
    } catch (error) {
      console.error('Error getting templates:', error);
      return [];
    }
  },

  /**
   * Get template by ID
   */
  async getTemplateById(id: string): Promise<IndustryTemplate | null> {
    try {
      const templateRef = doc(db, 'industryTemplates', id);
      const templateDoc = await getDoc(templateRef);
      
      if (!templateDoc.exists()) {
        return null;
      }
      
      const data = templateDoc.data();
      return {
        id: templateDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        lastOptimizationCheck: data.lastOptimizationCheck?.toDate(),
        lastUsedAt: data.lastUsedAt?.toDate(),
        versionHistory: (data.versionHistory || []).map((v: any) => ({
          ...v,
          createdAt: v.createdAt?.toDate()
        })),
        pendingSuggestions: (data.pendingSuggestions || []).map((s: any) => ({
          ...s,
          createdAt: s.createdAt?.toDate(),
          reviewedAt: s.reviewedAt?.toDate()
        }))
      } as IndustryTemplate;
    } catch (error) {
      console.error('Error getting template:', error);
      return null;
    }
  },

  /**
   * Get template for specific industry
   * Auto-matches based on detected industry
   */
  async getTemplateForIndustry(industry: string): Promise<IndustryTemplate | null> {
    try {
      // Normalize industry string
      const normalizedIndustry = this.normalizeIndustry(industry);
      
      if (!normalizedIndustry) {
        console.log(`ℹ️ No matching template for industry: ${industry}`);
        return null;
      }
      
      const templatesRef = collection(db, 'industryTemplates');
      const q = query(
        templatesRef,
        where('industry', '==', normalizedIndustry),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }
      
      // Return first active template for this industry
      const data = snapshot.docs[0].data();
      return {
        id: snapshot.docs[0].id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      } as IndustryTemplate;
    } catch (error) {
      console.error('Error getting template for industry:', error);
      return null;
    }
  },

  /**
   * Normalize industry string to IndustryType
   */
  normalizeIndustry(industry: string): IndustryType | null {
    const normalized = industry.toLowerCase().trim();
    
    // Automotive matches
    if (normalized.includes('automotive') || 
        normalized.includes('car dealer') || 
        normalized.includes('autohaus') ||
        normalized.includes('fahrzeug')) {
      return 'automotive';
    }
    
    // Finance matches
    if (normalized.includes('finance') || 
        normalized.includes('financial') ||
        normalized.includes('insurance') ||
        normalized.includes('versicherung') ||
        normalized.includes('finanzierung')) {
      return 'finance';
    }
    
    // E-Commerce matches
    if (normalized.includes('e-commerce') || 
        normalized.includes('ecommerce') ||
        normalized.includes('online shop') ||
        normalized.includes('retail')) {
      return 'ecommerce';
    }
    
    // Professional Services matches (catchall)
    if (normalized.includes('service') ||
        normalized.includes('consulting') ||
        normalized.includes('beratung') ||
        normalized.includes('software') ||
        normalized.includes('saas') ||
        normalized.includes('b2b')) {
      return 'professional_services';
    }
    
    return null;
  },

  /**
   * Create new template (Admin only)
   */
  async createTemplate(template: Omit<IndustryTemplate, 'id'>): Promise<string> {
    try {
      // Validate API v22 compliance before creating
      const validation = this.validateApiV22Compliance(template);
      if (!validation.isValid) {
        throw new Error(`Template is not API v22 compliant: ${validation.errors.join(', ')}`);
      }
      
      const templatesRef = collection(db, 'industryTemplates');
      const newTemplateRef = doc(templatesRef);
      
      await setDoc(newTemplateRef, {
        ...template,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        usageCount: 0,
        successRate: 0,
        currentVersion: template.version || '1.0',
        versionHistory: [{
          version: template.version || '1.0',
          markdownContent: template.markdownContent,
          changes: 'Initial version',
          createdAt: Timestamp.now(),
          createdBy: template.createdBy
        }],
        pendingSuggestions: []
      });
      
      console.log(`✅ Template created: ${template.name} (API v22 validated)`);
      return newTemplateRef.id;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  },

  /**
   * Update template (Admin only)
   */
  async updateTemplate(id: string, updates: Partial<IndustryTemplate>): Promise<void> {
    try {
      const templateRef = doc(db, 'industryTemplates', id);
      const existingDoc = await getDoc(templateRef);
      
      if (!existingDoc.exists()) {
        throw new Error('Template not found');
      }
      
      const existing = existingDoc.data() as IndustryTemplate;
      
      // Prevent updating system templates
      if (existing.isSystemTemplate && !updates.isSystemTemplate) {
        throw new Error('Cannot modify system templates');
      }
      
      // Validate if changing config
      if (updates.campaignConfig || updates.markdownContent) {
        const mergedTemplate = { ...existing, ...updates } as IndustryTemplate;
        const validation = this.validateApiV22Compliance(mergedTemplate);
        if (!validation.isValid) {
          throw new Error(`Update would break API v22 compliance: ${validation.errors.join(', ')}`);
        }
      }
      
      await updateDoc(templateRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      
      console.log(`✅ Template updated: ${id}`);
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  },

  /**
   * Delete template (Admin only, not for system templates)
   */
  async deleteTemplate(id: string): Promise<void> {
    try {
      const templateRef = doc(db, 'industryTemplates', id);
      const templateDoc = await getDoc(templateRef);
      
      if (!templateDoc.exists()) {
        throw new Error('Template not found');
      }
      
      const template = templateDoc.data() as IndustryTemplate;
      
      if (template.isSystemTemplate) {
        throw new Error('Cannot delete system templates');
      }
      
      await deleteDoc(templateRef);
      console.log(`✅ Template deleted: ${id}`);
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  },

  /**
   * Validate template against Google Ads API v22
   * Reference: https://developers.google.com/google-ads/api/fields/v22/overview
   */
  validateApiV22Compliance(template: Partial<IndustryTemplate>): TemplateValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Valid v22 Bidding Strategies (BiddingStrategyType enum)
    const validBiddingStrategies = [
      'target_cpa',
      'target_roas',
      'maximize_conversions',
      'maximize_conversion_value',
      'maximize_clicks',
      'manual_cpc',
      'manual_cpm',
      'manual_cpv'
    ];
    
    if (template.campaignConfig) {
      // Validate Search bidding strategy
      if (template.campaignConfig.searchBiddingStrategy && 
          !validBiddingStrategies.includes(template.campaignConfig.searchBiddingStrategy)) {
        errors.push(`Invalid Search bidding strategy: "${template.campaignConfig.searchBiddingStrategy}". Must be one of: ${validBiddingStrategies.join(', ')}`);
      }
      
      // Validate PMax bidding strategy
      if (template.campaignConfig.pmaxBiddingStrategy && 
          !validBiddingStrategies.includes(template.campaignConfig.pmaxBiddingStrategy)) {
        errors.push(`Invalid PMax bidding strategy: "${template.campaignConfig.pmaxBiddingStrategy}". Must be one of: ${validBiddingStrategies.join(', ')}`);
      }
      
      // Validate TARGET_ROAS requirements
      if (template.campaignConfig.searchBiddingStrategy === 'target_roas' || 
          template.campaignConfig.pmaxBiddingStrategy === 'target_roas') {
        if (!template.requirements?.minimumConversions || template.requirements.minimumConversions < 15) {
          warnings.push('TARGET_ROAS bidding requires at least 15-30 conversions in 30 days for optimal performance (Google Ads API v22 best practice)');
        }
        
        if (!template.campaignConfig.targetRoas || template.campaignConfig.targetRoas <= 0) {
          errors.push('targetRoas value is required when using TARGET_ROAS bidding strategy');
        }
      }
      
      // Validate TARGET_CPA requirements
      if (template.campaignConfig.searchBiddingStrategy === 'target_cpa' || 
          template.campaignConfig.pmaxBiddingStrategy === 'target_cpa') {
        if (!template.campaignConfig.targetCpa || template.campaignConfig.targetCpa <= 0) {
          errors.push('targetCpa value is required when using TARGET_CPA bidding strategy');
        }
      }
      
      // Validate budget allocation
      const totalBudget = (template.campaignConfig.searchBudgetPercent || 0) + 
                         (template.campaignConfig.pmaxBudgetPercent || 0);
      if (totalBudget !== 100) {
        errors.push(`Budget allocation must equal 100%. Current: ${totalBudget}% (Search: ${template.campaignConfig.searchBudgetPercent}%, PMax: ${template.campaignConfig.pmaxBudgetPercent}%)`);
      }
      
      // Budget must be positive percentages
      if (template.campaignConfig.searchBudgetPercent < 0 || template.campaignConfig.searchBudgetPercent > 100) {
        errors.push('Search budget percent must be between 0 and 100');
      }
      if (template.campaignConfig.pmaxBudgetPercent < 0 || template.campaignConfig.pmaxBudgetPercent > 100) {
        errors.push('PMax budget percent must be between 0 and 100');
      }
    }
    
    // Validate Merchant Center requirement
    if (template.requirements?.merchantCenter && template.industry !== 'ecommerce') {
      warnings.push('Merchant Center is typically only required for e-commerce campaigns');
    }
    
    // Validate minimum daily budget
    if (template.minimumDailyBudget && template.minimumDailyBudget < 1) {
      errors.push('Minimum daily budget must be at least 1 € (Google Ads API requirement)');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      apiVersion: 'v22'
    };
  },

  /**
   * Automatically improve template based on RAG data (Self-Learning)
   */
  async improveTemplateFromRag(orgId: string, industry: IndustryType): Promise<void> {
    try {
      console.log(`🧠 Self-Learning: Improving ${industry} template from RAG data...`);
      
      // 1. Get the current template
      const template = await this.getTemplateForIndustry(industry);
      if (!template) throw new Error(`No template found for ${industry}`);

      // 2. Fetch high-performing keywords from Knowledge Base
      const kbRef = collection(db, 'organizations', orgId, 'knowledgeBase', 'keywords', 'items');
      const q = query(
        kbRef, 
        where('industry', '==', industry.toLowerCase()),
        orderBy('performanceScore', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);
      const topKeywords = snapshot.docs.map(doc => doc.data().keyword);

      if (topKeywords.length < 5) {
        console.log('ℹ️ Not enough performance data yet to improve template.');
        return;
      }

      // 3. Use Gemini to synthesize new best practices
      const client = getClient();
      const prompt = `You are a Google Ads Expert. Improve this industry campaign template based on REAL PERFORMANCE DATA.

CURRENT TEMPLATE MARKDOWN:
${template.markdownContent}

REAL TOP PERFORMING KEYWORDS (from successful campaigns):
${topKeywords.join(', ')}

TASK:
1. Identify new keyword patterns from the top keywords.
2. Update the "Keywords Strategy" section in the markdown.
3. Suggest an improved "Target CPA" or "Daily Budget" if the data suggests it.
4. Return the ENTIRE updated markdown content.

Return ONLY the updated markdown text.`;

      const response = await client.models.generateContent({
        model: "gemini-2.5-flash",
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: { temperature: 0.2 }
      });

      const updatedMarkdown = response.text || '';

      // 4. Create a new version of the template
      const currentVersionParts = template.currentVersion.split('.');
      const newVersion = `${currentVersionParts[0]}.${parseInt(currentVersionParts[1]) + 1}`;

      await this.updateTemplate(template.id, {
        markdownContent: updatedMarkdown,
        currentVersion: newVersion,
        updatedAt: new Date(),
        versionHistory: [
          ...(template.versionHistory || []),
          {
            version: newVersion,
            markdownContent: updatedMarkdown,
            changes: `Auto-improved from RAG performance data (${topKeywords.length} winners)`,
            createdAt: new Date(),
            createdBy: 'system-rag'
          }
        ]
      } as any);

      console.log(`✅ Template ${industry} improved to v${newVersion} via Self-Learning!`);
    } catch (error) {
      console.error('Error improving template from RAG:', error);
    }
  },

  /**
   * Track template usage (increment counter)
   */
  async trackTemplateUsage(templateId: string): Promise<void> {
    try {
      const templateRef = doc(db, 'industryTemplates', templateId);
      await updateDoc(templateRef, {
        usageCount: increment(1),
        lastUsedAt: Timestamp.now()
      });
      
      console.log(`📊 Template usage tracked: ${templateId}`);
    } catch (error) {
      console.error('Error tracking template usage:', error);
    }
  },

  /**
   * Add pending optimization suggestions
   */
  async addPendingSuggestions(
    templateId: string, 
    suggestions: TemplateOptimizationSuggestion[]
  ): Promise<void> {
    try {
      const templateRef = doc(db, 'industryTemplates', templateId);
      const templateDoc = await getDoc(templateRef);
      
      if (!templateDoc.exists()) {
        throw new Error('Template not found');
      }
      
      const existingSuggestions = templateDoc.data().pendingSuggestions || [];
      
      await updateDoc(templateRef, {
        pendingSuggestions: [...existingSuggestions, ...suggestions],
        lastOptimizationCheck: Timestamp.now()
      });
      
      console.log(`💡 Added ${suggestions.length} optimization suggestions to template ${templateId}`);
    } catch (error) {
      console.error('Error adding suggestions:', error);
      throw error;
    }
  },

  /**
   * Apply optimization suggestion (creates new version)
   */
  async applySuggestion(
    templateId: string,
    suggestionId: string,
    adminUserId: string
  ): Promise<void> {
    try {
      const template = await this.getTemplateById(templateId);
      
      if (!template) {
        throw new Error('Template not found');
      }
      
      const suggestion = template.pendingSuggestions?.find(s => s.id === suggestionId);
      
      if (!suggestion) {
        throw new Error('Suggestion not found');
      }
      
      // Create new version
      const currentVersionParts = template.currentVersion.split('.');
      const newVersion = `${currentVersionParts[0]}.${parseInt(currentVersionParts[1]) + 1}`;
      
      // Apply change to markdown
      const updatedMarkdown = this.applyChangeToMarkdown(
        template.markdownContent,
        suggestion.section,
        suggestion.currentValue,
        suggestion.suggestedValue
      );
      
      const newVersionEntry = {
        version: newVersion,
        markdownContent: updatedMarkdown,
        changes: `Updated ${suggestion.section}: ${suggestion.suggestedValue}`,
        createdAt: new Date(),
        createdBy: adminUserId
      };
      
      // Update template
      await this.updateTemplate(templateId, {
        currentVersion: newVersion,
        markdownContent: updatedMarkdown,
        versionHistory: [...(template.versionHistory || []), newVersionEntry],
        pendingSuggestions: template.pendingSuggestions?.map(s => 
          s.id === suggestionId 
            ? { ...s, status: 'applied', reviewedBy: adminUserId, reviewedAt: new Date() }
            : s
        ),
        updatedBy: adminUserId
      });
      
      console.log(`✅ Template updated to v${newVersion}: ${template.name}`);
    } catch (error) {
      console.error('Error applying suggestion:', error);
      throw error;
    }
  },

  /**
   * Dismiss optimization suggestion
   */
  async dismissSuggestion(
    templateId: string,
    suggestionId: string,
    adminUserId: string
  ): Promise<void> {
    try {
      const template = await this.getTemplateById(templateId);
      
      if (!template) {
        throw new Error('Template not found');
      }
      
      await this.updateTemplate(templateId, {
        pendingSuggestions: template.pendingSuggestions?.map(s => 
          s.id === suggestionId 
            ? { ...s, status: 'dismissed', reviewedBy: adminUserId, reviewedAt: new Date() }
            : s
        )
      });
      
      console.log(`✅ Suggestion dismissed: ${suggestionId}`);
    } catch (error) {
      console.error('Error dismissing suggestion:', error);
      throw error;
    }
  },

  /**
   * Simple markdown text replacement (basic implementation)
   * For complex changes, admin should edit manually
   */
  applyChangeToMarkdown(
    markdown: string,
    section: string,
    oldValue: string,
    newValue: string
  ): string {
    // Simple replacement - can be enhanced with more sophisticated parsing
    return markdown.replace(oldValue, newValue);
  },

  /**
   * Update template performance metrics
   */
  async updatePerformanceMetrics(
    templateId: string,
    metrics: {
      avgCpa?: number;
      avgRoas?: number;
      successRate?: number;
    }
  ): Promise<void> {
    try {
      const templateRef = doc(db, 'industryTemplates', templateId);
      await updateDoc(templateRef, metrics);
      
      console.log(`📊 Performance metrics updated for template ${templateId}`);
    } catch (error) {
      console.error('Error updating metrics:', error);
    }
  }
};

